import { Trans, useLingui } from "@lingui/react/macro";
import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Link, Outlet, useMatches } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { HouseIcon, LayersIcon, SendIcon, TargetIcon, UsersRoundIcon } from "lucide-react";

import { LocaleSwitcher, RequireAuth, UserMenu } from "@/features/auth";
import { ProNavItem, ProPreview } from "@/features/billing";
import { DashboardNavItem } from "@/features/dashboard";
import { DrillsNavItem } from "@/features/drills";
import { HighlightsNavItem } from "@/features/highlights";
import { SellerDetails } from "@/features/legal";
import { PlayerRank, RecentMatchesNav } from "@/features/matches";
import { cn } from "@/shared/lib";
import { ConnectionIndicator } from "@/shared/realtime";
import { Button } from "@/shared/ui/button";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarToggle,
  SidebarTrigger,
} from "@/shared/ui/sidebar";

export type RouterContext = {
  queryClient: QueryClient;
};

const logoClassName =
  "font-display text-[26px] font-bold tracking-[0.04em] text-accent no-underline";

const footerLinkClassName = "text-accent no-underline hover:text-accent-strong";

// Подвал на всех страницах: соглашение и политика; тарифы и реквизиты исполнителя — только со
// ссылки с `?pro=1`, пока оплата не запущена (их проверяет модерация платёжного сервиса).
const SiteFooter = () => {
  const year = new Date().getFullYear();
  const { i18n } = useLingui();
  return (
    <footer className="border-t border-border bg-surface">
      <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-between gap-x-6 gap-y-2 px-6 py-4 text-[13px] text-muted">
        <span>
          <Trans>© {year} Relock</Trans>
        </span>
        <span className="flex flex-wrap gap-x-5 gap-y-2">
          {/* Публичные страницы сборок героев рендерит Django (backend/builds/api/pages), не SPA:
              обычная ссылка с полной загрузкой. По ней же поисковик находит эти страницы. */}
          <a
            href={i18n.locale === "ru" ? "/ru/heroes" : "/en/heroes"}
            className={footerLinkClassName}
          >
            <Trans>Hero builds</Trans>
          </a>
          <ProPreview>
            <Link to="/pricing" className={footerLinkClassName}>
              <Trans>Plans</Trans>
            </Link>
          </ProPreview>
          <Link to="/terms" className={footerLinkClassName}>
            <Trans>Agreement and offer</Trans>
          </Link>
          <Link to="/privacy" className={footerLinkClassName}>
            <Trans>Privacy policy</Trans>
          </Link>
        </span>
        <ProPreview>
          <SellerDetails className="w-full text-xs text-subtle" />
        </ProPreview>
        <p className="w-full text-xs text-subtle">
          <Trans>
            Relock is not affiliated with Valve. Deadlock and Steam are trademarks of Valve
            Corporation.
          </Trans>
        </p>
      </div>
    </footer>
  );
};

const SUPPORT_TELEGRAM_URL = "https://t.me/relockGG";

// Поддержка в Telegram — внизу меню над подвалом, на всех страницах после входа. В свёрнутом
// меню остаётся круглый значок с подсказкой (design/prototype/Sidebar.dc.html).
const SupportLink = () => {
  const { t } = useLingui();
  return (
    <SidebarMenuButton
      asChild
      size="lg"
      tooltip={t`Write to us on Telegram`}
      className="mt-auto min-h-14 rounded-[10px] border border-border bg-surface py-2 group-data-[state=collapsed]/sidebar:border-0 group-data-[state=collapsed]/sidebar:bg-transparent"
    >
      <a href={SUPPORT_TELEGRAM_URL} target="_blank" rel="noopener noreferrer">
        <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-sidebar-active">
          <SendIcon aria-hidden="true" className="size-4.5 text-accent" />
        </span>
        <span className="flex min-w-0 grow flex-col leading-tight">
          <span className="text-sm font-semibold">
            <Trans>Write to us on Telegram</Trans>
          </span>
          <span className="text-xs text-subtle">
            <Trans>Bug, idea or question</Trans>
          </span>
        </span>
      </a>
    </SidebarMenuButton>
  );
};

// Страницы без входа (вход, политика, тарифы, оферта): шапка с логотипом и языком, меню нет.
const PublicLayout = () => (
  <div className="flex min-h-dvh flex-col bg-background text-foreground">
    <header className="border-b border-border bg-surface">
      <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-between gap-x-7 gap-y-3 px-6 py-3.5">
        <Link to="/" className={logoClassName}>
          Relock
        </Link>
        <LocaleSwitcher />
      </div>
    </header>
    <div className="flex flex-1 flex-col">
      <Outlet />
    </div>
    <SiteFooter />
  </div>
);

// Страницы после входа — по макету design/prototype/Sidebar.dc.html: меню слева с разделами,
// последними матчами, связью, языком и игроком. Разделов четыре (макет «15г»,
// ReturnNoMatches.dc.html): «Матчи сразу» — строкой под последними матчами, профиль — в меню
// игрока внизу. Меню сворачивается до значков; на телефоне —
// полоса с «☰», меню выезжает шторкой (design/prototype/PhoneMenu.dc.html).
const AppLayout = () => {
  const { t } = useLingui();
  return (
    <RequireAuth>
      <SidebarProvider className="bg-background text-foreground">
        <Sidebar>
          <SidebarHeader>
            <Link
              to="/"
              aria-label="Relock"
              className={cn(logoClassName, "pl-2.5 group-data-[state=collapsed]/sidebar:pl-0")}
            >
              <span className="group-data-[state=collapsed]/sidebar:hidden">Relock</span>
              <img
                src="/favicon.svg"
                alt=""
                className="hidden size-10 group-data-[state=collapsed]/sidebar:block"
              />
            </Link>
            <SidebarToggle />
          </SidebarHeader>
          <SidebarContent>
            <SidebarGroup aria-label={t`Sections`}>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip={t`Home`}>
                    <Link to="/" activeOptions={{ exact: true }}>
                      <HouseIcon aria-hidden="true" />
                      <span>
                        <Trans>Home</Trans>
                      </span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <HighlightsNavItem />
                <DrillsNavItem />
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip={t`Play style`}>
                    <Link to="/style">
                      <TargetIcon aria-hidden="true" />
                      <span>
                        <Trans>Play style</Trans>
                      </span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip={t`Builds`}>
                    <Link to="/builds">
                      <LayersIcon aria-hidden="true" />
                      <span>
                        <Trans>Builds</Trans>
                      </span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip={t`Friends`}>
                    <Link to="/friends">
                      <UsersRoundIcon aria-hidden="true" />
                      <span>
                        <Trans>Friends</Trans>
                      </span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <ProNavItem />
              </SidebarMenu>
            </SidebarGroup>
            <DashboardNavItem />
            <RecentMatchesNav />
            <SupportLink />
          </SidebarContent>
          <SidebarFooter>
            <div className="flex items-center justify-between gap-2 pl-2.5 group-data-[state=collapsed]/sidebar:flex-col group-data-[state=collapsed]/sidebar:pl-0">
              <ConnectionIndicator />
              <LocaleSwitcher className="group-data-[state=collapsed]/sidebar:flex-col" />
            </div>
            <UserMenu details={<PlayerRank />} />
          </SidebarFooter>
        </Sidebar>
        <SidebarInset>
          <header className="sticky top-0 z-40 flex items-center gap-3 border-b border-border bg-sidebar px-3 py-2 md:hidden">
            <SidebarTrigger />
            <Link to="/" className={logoClassName}>
              Relock
            </Link>
            <span className="grow" />
            <ConnectionIndicator />
          </header>
          <div className="flex flex-1 flex-col">
            <Outlet />
          </div>
          <SiteFooter />
        </SidebarInset>
      </SidebarProvider>
    </RequireAuth>
  );
};

// Все страницы, кроме помеченных `staticData.isPublic` (вход, политика, тарифы, оферта), — только
// после входа — так защищены и маршруты других фич.
const RootLayout = () => {
  const isPublic = useMatches({
    select: (matches) => matches.some((match) => match.staticData.isPublic === true),
  });
  return (
    <>
      {isPublic ? <PublicLayout /> : <AppLayout />}
      {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
    </>
  );
};

// Неизвестный адрес: страница 404 внутри оболочки (для гостя — после входа, как любая
// закрытая страница).
const NotFoundPage = () => (
  <main
    data-page="not-found"
    className="mx-auto flex w-full max-w-[640px] grow flex-col items-center justify-center gap-4 px-6 py-16 text-center"
  >
    <p className="font-mono text-sm tracking-[0.2em] text-subtle">404</p>
    <h1 className="font-display text-[40px] leading-[1.1]">
      <Trans>Page not found</Trans>
    </h1>
    <p className="max-w-[48ch] text-base text-muted">
      <Trans>The link may be outdated or contain a typo. Your matches are on the home page.</Trans>
    </p>
    <Button asChild>
      <Link to="/" className="no-underline">
        <Trans>Go to home page</Trans>
      </Link>
    </Button>
  </main>
);

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFoundPage,
});
